import { Alerts } from "../../../../utils/Alerts.js";
import { RPCs } from "../../../../services/rpc.js";
import { DateTime as DateTime$1 } from "../../../../components/DateTime/index.js";
import { useRPCContext } from "../../../../contexts/RPCContext.js";
import { useRPC } from "../../../../hooks/useRPC/index.js";
import { useRPCServerEventHandler } from "../../../../hooks/useRPCServerEventHandler/index.js";
import { BillingContactFormModal } from "./components/BillingContactFormModal.js";
import { BillingContact } from "./components/BillingContact.js";
import { useMemo, useState } from "react";
import { Button, HorizontalScroll, Icon, Loading } from "@therms/atalaya";
import { IoCheckbox } from "react-icons/io5";
import { IoMdAdd } from "react-icons/io";
import { jsx, jsxs } from "react/jsx-runtime";
import classnames from "classnames";
import { MdOpenInNew } from "react-icons/md";
// src/modules/Organizations/shared/Subcription/index.tsx
function ManageOrganizationSubscription() {
	const [loadingPortalLink, setLoadingPortalLink] = useState(false);
	const [loadingContact, setLoadingContact] = useState(false);
	const { rpcService } = useRPCContext();
	const [loading, setLoading] = useState(false);
	const [showContactFormModal, setShowContactFormModal] = useState(void 0);
	const editContactFormModal = typeof showContactFormModal === "object";
	const { call, data, isCalling } = useRPC(RPCs.Organizations.GetOrganizationSubscriptionDetails.v1);
	const { data: activeUsersData } = useRPC(RPCs.Users.GetUsersList.v1, {
		active: true,
		pagination: { limit: 0 }
	});
	const billingContacts = useMemo(() => data?.subscriptionDetails?.billingContacts, [data?.subscriptionDetails?.billingContacts, data?.subscriptionDetails?.billingContacts.length]);
	const handleBillingSetupClick = () => {
		setLoadingPortalLink(true);
		rpcService.Organizations.GetOrganizationStripeBillingPortalLink.v1.call({}).then((response) => window.location.href = response.data.link).catch((err) => {
			Alerts.error("There was a problem opening the Billing Portal", err.message);
			console.log("handleBillingSetupClick() err", err);
		}).finally(() => {
			setLoadingPortalLink(false);
		});
	};
	const handleSaveContact = (contact, replaceBillingContactIndex) => {
		setLoadingContact(true);
		const newContacts = billingContacts || [];
		if (typeof replaceBillingContactIndex === "number") newContacts.splice(replaceBillingContactIndex, 1, contact);
		else newContacts.push(contact);
		rpcService.Organizations.UpdateOrganizationOrganizationBillingContacts.v1.call({ billingContacts: newContacts }).catch((err) => {
			Alerts.error("There was a problem saving the billing contacts", err.message);
			console.log("handleSaveContact() err", err);
		}).finally(() => {
			setShowContactFormModal(void 0);
			setLoadingContact(false);
			call();
		});
	};
	const handleDeleteContact = (indexToDelete) => {
		if (billingContacts) {
			const newContacts = billingContacts;
			newContacts.splice(indexToDelete, 1);
			rpcService.Organizations.UpdateOrganizationOrganizationBillingContacts.v1.call({ billingContacts: newContacts }).catch((err) => {
				Alerts.error("There was a problem removing the billing contact", err.message);
				console.log("handleNewContact() err", err);
			}).finally(() => {
				setShowContactFormModal(void 0);
				call();
			});
		}
	};
	const handleAccountActivate = () => {
		setLoading(true);
		rpcService.Organizations.UpgradeOrganizationPlanToCore.v1.call().finally(() => setLoading(false));
	};
	useRPCServerEventHandler("OrganizationUpdated", call);
	if (!data?.subscriptionDetails) return null;
	const { subscriptionDetails: { plan, stripe: { billingIsSetup } } } = data;
	const showActivateButton = billingIsSetup && [
		"cancelled",
		"free",
		"trial"
	].includes(plan);
	return /* @__PURE__ */ jsxs("div", { children: [
		isCalling && /* @__PURE__ */ jsx(Loading, { overlay: true }),
		billingIsSetup ? /* @__PURE__ */ jsx("div", {
			className: "mb-xs bg-main-faded bg-surface px-base py-xs rounded font-medium",
			children: /* @__PURE__ */ jsxs("div", {
				className: "gap-xs flex flex-wrap items-baseline",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "grow",
					children: [
						/* @__PURE__ */ jsxs("span", {
							className: "text-color-strong",
							children: [/* @__PURE__ */ jsx(Icon, {
								color: "positive",
								Icon: IoCheckbox
							}), " Billing Setup"]
						}),
						" ",
						/* @__PURE__ */ jsx("span", {
							className: "text-color-neutral text-sm",
							children: "Use the Billing Portal to update credit card information and download past invoices"
						})
					]
				}), /* @__PURE__ */ jsxs(Button, {
					loading: loadingPortalLink,
					onClick: handleBillingSetupClick,
					variant: "main",
					children: ["Billing Portal", /* @__PURE__ */ jsx(Icon, {
						Icon: MdOpenInNew,
						size: "sm",
						className: "ml-xs"
					})]
				})]
			})
		}) : /* @__PURE__ */ jsxs("div", {
			className: "mb-xs gap-xs bg-caution-faded px-base py-xs text-color-strong flex flex-wrap items-center justify-between rounded font-medium",
			children: [/* @__PURE__ */ jsx("span", { children: "Billing information is not setup, add it to prevent service interruption." }), /* @__PURE__ */ jsxs(Button, {
				loading: loadingPortalLink,
				onClick: handleBillingSetupClick,
				variant: "caution",
				children: ["Setup", /* @__PURE__ */ jsx(Icon, {
					Icon: MdOpenInNew,
					size: "sm",
					className: "ml-xs"
				})]
			})]
		}),
		/* @__PURE__ */ jsxs("div", {
			className: "mb-xl gap-xs bg-surface px-base py-xs flex flex-wrap rounded-sm font-medium",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "grow",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "gap-xs flex flex-wrap items-baseline",
					children: [
						/* @__PURE__ */ jsxs("div", {
							className: "gap-y-xxs flex flex-col",
							children: [/* @__PURE__ */ jsx("span", {
								className: "text-color-subtle text-sm tracking-wide uppercase",
								children: "Subscription Status"
							}), /* @__PURE__ */ jsx("div", {
								className: "text-color-subtle text-sm tracking-wide uppercase",
								children: "Active Users"
							})]
						}),
						/* @__PURE__ */ jsxs("div", {
							className: "flex flex-col",
							children: [/* @__PURE__ */ jsx("span", {
								className: classnames({
									"text-color-caution": plan === "trial",
									"text-color-neutral": plan === "cancelled",
									"text-color-positive": plan === "core"
								}, "font-bold tracking-wide capitalize"),
								children: plan === "core" ? "active" : plan
							}), /* @__PURE__ */ jsx("span", { children: activeUsersData?.total })]
						}),
						plan === "trial" && data.subscriptionDetails.trial && /* @__PURE__ */ jsxs("span", {
							className: "text-color-subtle text-sm",
							children: [
								data.subscriptionDetails.trial.endDate > (/* @__PURE__ */ new Date()).toISOString() ? "Ends on " : "Ended ",
								/* @__PURE__ */ jsx(DateTime$1, {
									mode: "date",
									timestamp: data.subscriptionDetails.trial.endDate
								}),
								" ",
								"(",
								/* @__PURE__ */ jsx(DateTime$1, {
									mode: "date",
									relative: true,
									timestamp: data.subscriptionDetails.trial.endDate
								}),
								")"
							]
						})
					]
				}), billingIsSetup && plan !== "core" && /* @__PURE__ */ jsxs("div", { children: [
					plan === "trial" && "To prevent service interruption activate your organization before your trial expires. ",
					plan === "cancelled" && "To use the system again please activate your organization. ",
					"Billing will start once your organization gets activated."
				] })]
			}), showActivateButton && /* @__PURE__ */ jsx("div", {
				className: "inline-flex items-center justify-center",
				children: /* @__PURE__ */ jsx(Button, {
					disabled: loading,
					onClick: handleAccountActivate,
					variant: "positive",
					children: "Activate"
				})
			})]
		}),
		/* @__PURE__ */ jsx("div", {
			className: "mb-base text-lg font-medium",
			children: "Billing Contacts"
		}),
		billingContacts && /* @__PURE__ */ jsxs(HorizontalScroll, {
			itemWidth: "250px",
			children: [/* @__PURE__ */ jsxs("div", {
				onClick: () => setShowContactFormModal(true),
				className: "border-border bg-surface-subtle p-base text-color-subtle hover:bg-surface-strong focus:bg-surface-strong active:bg-background flex cursor-pointer flex-col items-center justify-center rounded border-4 border-dashed transition",
				children: [/* @__PURE__ */ jsx(Icon, {
					Icon: IoMdAdd,
					size: "lg",
					color: "positive"
				}), /* @__PURE__ */ jsx("span", {
					className: "font-medium",
					children: "New Billing Contact"
				})]
			}), billingContacts.map((contact, index) => /* @__PURE__ */ jsx(BillingContact, {
				email: contact.email,
				name: contact.name,
				phone: contact.phone,
				onDelete: () => handleDeleteContact(index),
				onEdit: () => {
					setShowContactFormModal({
						contact,
						index
					});
				}
			}, index))]
		}),
		showContactFormModal && /* @__PURE__ */ jsx(BillingContactFormModal, {
			contact: editContactFormModal ? showContactFormModal.contact : void 0,
			closeHandler: () => {
				setShowContactFormModal(void 0);
			},
			isSubmitting: loadingContact,
			onSubmit: (contact) => handleSaveContact(contact, editContactFormModal ? showContactFormModal.index : void 0)
		})
	] });
}
export { ManageOrganizationSubscription };

//# sourceMappingURL=index.js.map